/**
 * MovimientosColaboradorApiClient -- danos y entradas de ajuste por colaborador.
 *
 * MISION CIERRE-CONTEO-V1-001, tarea CC-COLAB-T3.
 *
 * Fuente: GET /api/v1/businesses/{businessId}/movimientos-inventario/por-colaborador
 * publicado por Argy-A en el PR nexo-api #177, contra el contrato que pase en la
 * bitacora #9571. Las claves de aqui son las de ESE contrato: cambiarlas de un lado
 * rompe el otro.
 *
 * FAIL-OPEN (patron PANEL-AUDIOS/PRESU y Sala de Maquinas): mientras el endpoint no
 * este desplegado, esto devuelve un motivo legible en vez de romperse, y la vista lo
 * dice en pantalla con su causa. El dia que se mergee y despliegue, se enciende sola
 * SIN re-deploy del panel.
 *
 * Al LEER se toleran las dos formas (camelCase del DTO y snake_case de la vista de
 * BD). Cuesta una linea y protege: la lectura perdona, la escritura no.
 *
 * @module infrastructure/api/MovimientosColaboradorApiClient
 */

import { ApiBase } from './ApiBase.js';

const ruta = (bizId) =>
  `/api/v1/businesses/${encodeURIComponent(bizId)}/movimientos-inventario/por-colaborador`;

/** Primer valor no nulo entre varias claves posibles. */
const pick = (o, ...claves) => {
  for (const k of claves) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return null;
};
const num = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v));

export const MovimientosColaboradorApiClient = {
  /**
   * Resumen por colaborador de un periodo. Nunca lanza.
   *
   * @param {string} businessId
   * @param {{desde?:string, hasta?:string}} [opts] fechas yyyy-MM-dd
   * @returns {Promise<{ok:boolean, data?:object, motivo?:string, detalle?:string}>}
   */
  async getPorColaborador(businessId, opts = {}) {
    if (!businessId) {
      return { ok: false, motivo: 'SIN_NEGOCIO', detalle: 'elige un negocio para ver sus movimientos' };
    }

    const qs = new URLSearchParams();
    if (opts.desde) qs.set('desde', opts.desde);
    if (opts.hasta) qs.set('hasta', opts.hasta);
    const url = ruta(businessId) + (qs.toString() ? `?${qs}` : '');

    let res;
    try {
      res = await ApiBase.get(url);
    } catch (err) {
      return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' };
    }

    if (!res.ok) {
      // El motivo importa: un mensaje unico para todos los fallos hace dudar de lo
      // que si funciona (la captura de Josse del 26-ago).
      const txt = JSON.stringify(res.error || '');
      if (/not\s*found|404/i.test(txt)) {
        return {
          ok: false,
          motivo: 'NO_PUBLICADO',
          detalle: 'el resumen por colaborador todavia no esta publicado (nexo-api PR #177)'
        };
      }
      if (/401|unauthor/i.test(txt)) {
        return { ok: false, motivo: 'SIN_SESION', detalle: 'la sesion no tiene permiso para leer estos movimientos' };
      }
      if (/403|forbidden/i.test(txt)) {
        return { ok: false, motivo: 'PROHIBIDO', detalle: 'este resumen es solo para el dueno o el administrativo' };
      }
      return {
        ok: false,
        motivo: 'ERROR_API',
        detalle: (typeof res.error === 'string' ? res.error : res.error?.message) || 'error de la API'
      };
    }

    const d = res.data?.data ?? res.data ?? {};
    const arr = (x) => (Array.isArray(x) ? x : []);

    return {
      ok: true,
      data: {
        desde: pick(d, 'desde', 'from'),
        hasta: pick(d, 'hasta', 'to'),
        // TRES CAPAS (bitacora #9589 y #9592). No es un detalle tecnico: solo la capa
        // -conteo- se puede usar para comparar personas, porque las otras dos atribuyen
        // por quien tenia la sesion abierta, no por quien hizo el movimiento.
        atribuidoPorConteo: num(pick(d, 'atribuidoPorConteo', 'atribuido_por_conteo')),
        atribuidoPorUsuario: num(pick(d, 'atribuidoPorUsuario', 'atribuido_por_usuario')),
        sinIdentificar: num(pick(d, 'sinIdentificar', 'sin_identificar')),
        fiabilidadPct: num(pick(d, 'fiabilidadPct', 'fiabilidad_pct')),
        conDueno: num(pick(d, 'conDueno', 'con_dueno')),
        sinDueno: num(pick(d, 'sinDueno', 'sin_dueno')),
        coberturaPct: num(pick(d, 'coberturaPct', 'cobertura_pct')),
        advertencia: pick(d, 'advertencia', 'warning') || '',
        // SUJ-T3 (nexo-api 293): TODOS los danos del periodo en pesos, con la regla del Desktop.
        // NO es la suma de las filas (una fila puede contar doble con 2 operadores, #17141).
        // null = el API aun no lo manda (antes del 293): la tarjeta lo dice.
        danosPesosTotal: pick(d, 'danosPesosTotal', 'danos_pesos_total') === null ? null : num(pick(d, 'danosPesosTotal', 'danos_pesos_total')),
        items: arr(pick(d, 'items', 'filas')).map((f) => ({
          colaboradorId: pick(f, 'colaboradorId', 'colaborador_id'),
          colaborador: pick(f, 'colaborador', 'nombre') || 'Sin identificar',
          capa: pick(f, 'capa', 'layer') || '',
          danosUnidades: num(pick(f, 'danosUnidades', 'danos_unidades')),
          danosMovimientos: num(pick(f, 'danosMovimientos', 'danos_movimientos')),
          danosPesos: num(pick(f, 'danosPesos', 'danos_pesos')),
          entradasAjusteUnidades: num(pick(f, 'entradasAjusteUnidades', 'entradas_ajuste_unidades')),
          entradasAjusteMovimientos: num(pick(f, 'entradasAjusteMovimientos', 'entradas_ajuste_movimientos'))
        }))
      }
    };
  }
};
